import React, { useEffect, useState } from "react";

import {
  getDashboardAssessmentBannerSrc,
  isAssessmentType,
  snakeToTitleCase,
  toCamelCase,
} from "../../utils/utility-functions";
import {
  ADHD_TEST_ASSESSMENT_ID,
  ANTISOCIAL_PERSONALITY_DISORDER_ASSESSMENT_ID,
  ASSESSMENT_OVERVIEW_CONTENT_KEY,
  ATTACHMENT_STYLE_TEST_ASSESSMENT_ID,
  AUTISM_SPECTRUM_TEST_ASSESSMENT_ID,
  BIG_FIVE_FACETS_TEST_ASSESSMENT_ID,
  BIG_FIVE_TEST_ASSESSMENT_ID,
  BIPOLAR_DISORDER_ASSESSMENT_ID,
  CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
  CAREER_TEST_ASSESSMENT_ID,
  CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID,
  COLOR_BLINDNESS_TEST_ASSESSMENT_ID,
  DEPRESSION_TEST_ASSESSMENT_ID,
  DIFFICULT_PERSON_TEST_ASSESSMENT_ID,
  DISC_TEST_ASSESSMENT_ID,
  DYSLEXIA_SCREENING_ADULT_TEST_ASSESSMENT_ID,
  DYSLEXIA_SCREENING_CHILDREN_TEST_ASSESSMENT_ID,
  EMOTIONAL_INTELLIGENCE_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  FOUR_TEMPERAMENTS_PERSONALITY_TEST_ASSESSMENT_ID,
  getAssessmentOverviewModelType,
  HIGHLY_SENSITIVE_PERSON_TEST_ASSESSMENT_ID,
  IQ_TEST_ASSESSMENT_ID,
  LIKEABILITY_TEST_ASSESSMENT_ID,
  LOVE_STYLE_TEST_ASSESSMENT_ID,
  NARCISSISTIC_PERSONALITY_DISORDER_ASSESSMENT_ID,
  PERSONALITY_DISORDER_TEST_ASSESSMENT_ID,
  PERSONALITY_TEST_ASSESSMENT_ID,
  PSYCHOPATH_ASSESSMENT_ID,
  ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
  SOCIAL_ANXIETY_TEST_ASSESMENT_ID,
  AUTISM_CHILDREN_TEST_ASSESSMENT_ID,
  SOCIOPATH_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";
import {
  deriveBandKeyFromPercentage,
  deriveOverallPercentage,
  deriveOverallRawScore,
} from "../../pages/iq-report/iq-calculations";
import { TOTAL_QUESTIONS } from "../../pages/iq-report/iq-assessment-config";
import {
  attachmentStyleScore,
  calculateBigFiveFromFacets,
  calculateBipolarScore,
  calculateTotalScoreDepression,
  childhoodTraumaScore,
  computeProvisionalSociopathClassification,
  computeProvisionalSociopathOverallPercentage,
  findColorBlingHighestPercentageTrait,
  fourTemperamentFindPersonalityBlend,
  fourTemperamentTraitCombination,
  generateContentKeyForCareerPreference,
  getAdhdScoreCategory,
  getAntisocialPersonalityDisorderLevel,
  getAustismSpecturmOverAllLevelDetails,
  getBigFiveTraitDetailsWithLabels,
  getBipolarResultCategory,
  getContentData,
  getDepressionLevel,
  getDifficultyLevel,
  getDiscPersonalityDetailsWithLabel,
  getDyslexiaAdultDetailsWithLabel,
  getDyslexiaChildrenDetailsWithLabel,
  getEmotioanlIntelligenceOverAllScoreLevel,
  getEmotioanlIntelligenceTraitScoresWithTotal,
  getHighlySensitiveLevel,
  getLikeabilityDetailsWithLabel,
  getLoveStyleDetailsWithLabel,
  getOverAllNarcissisticPersonalityLevel,
  getPanelData,
  getPersonalityDisorderDetailWithLabel,
  getPsychopathDetailWithLable,
  getRomanticCompatiblityDetailsWithLabel,
  likeabilityScore,
  socialAnxietyScore,
  transformAdhdUserTraitsData,
  transformAntisocialPersonalityDisorderUserTraitsData,
  transformAutismSpectrumUserTraitsData,
  transformDifficultPersonUserTraitsData,
  transformHighlySensitiveUserTraitsData,
  transformNarcissisticPersonalityUserTraitsData,
  autismChildrenScore,
} from "../../utils/assessment-utils";
import SectionLoader from "../section-loader";
import DashboardAssessmentImage from "./dashboard-assessment-image";

export const AssessmentOverview = ({
  selectedAssessment,
  isPremiumOrGuest,
  handleViewReport,
  handleShareReport,
  handleDownloadReport,
  handleDeleteReport,
  handleDeleteClick,
  convertToLowerCase,
  ShareReportIcon,
  DownloadReportIcon,
  DeleteReportIcon,
  t,
  getAssessmentResultByAssessmentAttemptIdConnect,
  currentLocale,
  getInsightfulContentConnect,
}) => {
  // Get overview banner image — shared with the mobile assessment-detail
  // sheet in dashboard-b/component.js via the same helper, so the two can
  // never resolve different URLs for the same assessment.
  const overviewBannerImage = getDashboardAssessmentBannerSrc(
    selectedAssessment?.assessmentId,
  );

  // Get the selected assessment's title from the assessment data
  const selectedAssessmentTitle = selectedAssessment?.title || "";

  return (
    <div className="text-gray-600 flex flex-col justify-start items-start rounded-[20px] border-[1px] border-insightful-blue overflow-hidden shadow-sm h-full">
      <div
        className="relative w-full min-h-[90px] mb-2 bg-gradient-to-r from-purple-500 to-pink-500
         rounded-t-lg rounded-b-none pl-2 pr-4 py-4 lg:py-6 text-white flex items-center"
        id="Assessmentcard-header"
      >
        {/* Icon */}
        <DashboardAssessmentImage
          src={overviewBannerImage}
          alt={selectedAssessmentTitle ? `${selectedAssessmentTitle} icon` : ""}
          className="w-14 h-14 object-contain flex-shrink-0 mr-3"
        />

        {/* Title */}
        {selectedAssessmentTitle && (
          <div className="text-left flex-1 min-w-0">
            <span className="block text-white text-base sm:text-lg lg:text-xl font-bold leading-tight  break-words">
              {selectedAssessmentTitle}
            </span>
          </div>
        )}
      </div>

      <AssessmentDetailsBodyContent
        selectedAssessment={selectedAssessment}
        isPremiumOrGuest={isPremiumOrGuest}
        handleViewReport={handleViewReport}
        convertToLowerCase={convertToLowerCase}
        t={t}
        getAssessmentResultByAssessmentAttemptIdConnect={
          getAssessmentResultByAssessmentAttemptIdConnect
        }
        currentLocale={currentLocale}
        getInsightfulContentConnect={getInsightfulContentConnect}
      />

      {/* Divider - moved outside the reusable component */}
      <hr className="w-full border-t border-gray-200 mt-2" />

      {/* Bottom Action Icons - kept outside the reusable component */}
      <div
        className="w-full flex justify-between items-center gap-6 px-2 py-1 flex-shrink-0"
        style={{ minHeight: "32px", maxHeight: "32px" }}
      >
        {/* Share */}
        <img
          src={ShareReportIcon}
          alt="Share"
          className="w-6 h-6 cursor-pointer"
          onClick={() =>
            handleShareReport(isPremiumOrGuest, selectedAssessment)
          }
        />

        {/* Download ONLY for personality test */}
        {selectedAssessment &&
          convertToLowerCase(selectedAssessment.assessmentId) ===
            convertToLowerCase(PERSONALITY_TEST_ASSESSMENT_ID) && (
            <img
              src={DownloadReportIcon}
              alt="Download"
              className="w-6 h-6 cursor-pointer"
              onClick={() =>
                handleDownloadReport(
                  isPremiumOrGuest,
                  selectedAssessment.assessmentAttemptId,
                )
              }
            />
          )}

        {/* Delete */}
        <img
          src={DeleteReportIcon}
          alt="Delete"
          className="w-6 h-6 cursor-pointer"
          onClick={() =>
            handleDeleteClick
              ? handleDeleteClick(
                  isPremiumOrGuest,
                  selectedAssessment.assessmentId,
                  selectedAssessment.assessmentAttemptId,
                  selectedAssessment.id,
                )
              : handleDeleteReport(
                  isPremiumOrGuest,
                  selectedAssessment.assessmentId,
                  selectedAssessment.assessmentAttemptId,
                  selectedAssessment.id,
                )
          }
        />
      </div>
    </div>
  );
};

// Reusable component for Assessment Details Body Content
// This component now handles all calculations internally
export const AssessmentDetailsBodyContent = ({
  selectedAssessment,
  isPremiumOrGuest,
  handleViewReport,
  convertToLowerCase,
  t,
  getAssessmentResultByAssessmentAttemptIdConnect,
  currentLocale,
  getInsightfulContentConnect,
}) => {
  const [currentUserTraits, setCurrentUserTraits] = useState([]);
  const personalityNameEnglishCode =
    selectedAssessment?.nameEnglishCode || "commander";
  const personalityTitle = selectedAssessment?.personality?.title;
  const [isLoadingAssessment, setIsLoadingAssessment] = useState(false);
  const [insightfulContent, setInsightfulContent] = useState(null);

  const assessmentAttemptId = selectedAssessment?.assessmentAttemptId;

  useEffect(() => {
    if (!assessmentAttemptId) return;
    const fetchAssessmentResult = async () => {
      try {
        setIsLoadingAssessment(true);
        const userAssessmentResult =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );
        const modelType = getAssessmentOverviewModelType(
          selectedAssessment?.assessmentId,
        )?.[currentLocale];

        if (modelType) {
          const data = {
            modelType,
            contentKey: ASSESSMENT_OVERVIEW_CONTENT_KEY,
          };
          const insightfulContent = await getInsightfulContentConnect(data);
          setInsightfulContent(insightfulContent);
        }

        if (userAssessmentResult.userTraits) {
          setIsLoadingAssessment(false);
          setCurrentUserTraits(userAssessmentResult.userTraits);
        }
      } catch (error) {
        console.error("Error fetching assessment result:", error);
        setIsLoadingAssessment(false);
      }
    };
    fetchAssessmentResult();
  }, [
    assessmentAttemptId,
    currentLocale,
    getAssessmentResultByAssessmentAttemptIdConnect,
    getInsightfulContentConnect,
    selectedAssessment?.assessmentId,
  ]);

  if (isLoadingAssessment) {
    return <SectionLoader isLoading={isLoadingAssessment} />;
  }

  const contentItems = insightfulContent?.content?.items || [];

  const assessmentId = selectedAssessment?.assessmentId;

  // Assessment type checks
  const isBigFiveAssessment = isAssessmentType(
    assessmentId,
    BIG_FIVE_TEST_ASSESSMENT_ID,
  );
  const isPersonalityTestAssessment = isAssessmentType(
    assessmentId,
    PERSONALITY_TEST_ASSESSMENT_ID,
  );
  const isColorBlindAssessment = isAssessmentType(
    assessmentId,
    COLOR_BLINDNESS_TEST_ASSESSMENT_ID,
  );
  const isLoveStyleAssessment = isAssessmentType(
    assessmentId,
    LOVE_STYLE_TEST_ASSESSMENT_ID,
  );
  const isPersonalityDisorder = isAssessmentType(
    assessmentId,
    PERSONALITY_DISORDER_TEST_ASSESSMENT_ID,
  );
  const isDepresssionAssessment = isAssessmentType(
    assessmentId,
    DEPRESSION_TEST_ASSESSMENT_ID,
  );
  const isDifficultPersonAssessment = isAssessmentType(
    assessmentId,
    DIFFICULT_PERSON_TEST_ASSESSMENT_ID,
  );
  const isHighlySensitiveAssessment = isAssessmentType(
    assessmentId,
    HIGHLY_SENSITIVE_PERSON_TEST_ASSESSMENT_ID,
  );
  const isAttachmentStyle = isAssessmentType(
    assessmentId,
    ATTACHMENT_STYLE_TEST_ASSESSMENT_ID,
  );
  const isDyslexiaChlidrenAssessment = isAssessmentType(
    assessmentId,
    DYSLEXIA_SCREENING_CHILDREN_TEST_ASSESSMENT_ID,
  );
  const isDyslexiaAdultAssessment = isAssessmentType(
    assessmentId,
    DYSLEXIA_SCREENING_ADULT_TEST_ASSESSMENT_ID,
  );
  const isRomanticCompatibilityAssessment = isAssessmentType(
    assessmentId,
    ROMANTIC_COMPATIBILITY_ASSESSMENT_ID,
  );
  const isLikeabilityAssessment = isAssessmentType(
    assessmentId,
    LIKEABILITY_TEST_ASSESSMENT_ID,
  );
  const isNarcissisticPersonalityAssessment = isAssessmentType(
    assessmentId,
    NARCISSISTIC_PERSONALITY_DISORDER_ASSESSMENT_ID,
  );
  const isEmotionalIntelligenceAssessment = isAssessmentType(
    assessmentId,
    EMOTIONAL_INTELLIGENCE_TEST_ASSESSMENT_ID,
  );
  const isFourTemperamentsAssessment = isAssessmentType(
    assessmentId,
    FOUR_TEMPERAMENTS_PERSONALITY_TEST_ASSESSMENT_ID,
  );
  const isCareerPreferenceAssessment = isAssessmentType(
    assessmentId,
    CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
  );
  const isBigFiveFacetsAssessment = isAssessmentType(
    assessmentId,
    BIG_FIVE_FACETS_TEST_ASSESSMENT_ID,
  );
  const isAdhdAssessment = isAssessmentType(
    assessmentId,
    ADHD_TEST_ASSESSMENT_ID,
  );
  const isAntiSocialAssessment = isAssessmentType(
    assessmentId,
    ANTISOCIAL_PERSONALITY_DISORDER_ASSESSMENT_ID,
  );
  const isAutismSpectrumAssessment = isAssessmentType(
    assessmentId,
    AUTISM_SPECTRUM_TEST_ASSESSMENT_ID,
  );
  const isBipolarAssessment = isAssessmentType(
    assessmentId,
    BIPOLAR_DISORDER_ASSESSMENT_ID,
  );
  const isEnneagramAssessment = isAssessmentType(
    assessmentId,
    ENNEAGRAM_TEST_ASSESSMENT_ID,
  );
  const isDiscAssessment = isAssessmentType(
    assessmentId,
    DISC_TEST_ASSESSMENT_ID,
  );
  const isCareerTestAssessment = isAssessmentType(
    assessmentId,
    CAREER_TEST_ASSESSMENT_ID,
  );

  const isPsychopathTestAssessment = isAssessmentType(
    assessmentId,
    PSYCHOPATH_ASSESSMENT_ID,
  );
  const isChildhoodTrauma = isAssessmentType(
    assessmentId,
    CHILDHOOD_TRAUMA_TEST_ASSESSMENT_ID,
  );

  const isSocialAnxiety = isAssessmentType(
    assessmentId,
    SOCIAL_ANXIETY_TEST_ASSESMENT_ID,
  );

  const isAutismChild = isAssessmentType(
    assessmentId,
    AUTISM_CHILDREN_TEST_ASSESSMENT_ID,
  );

  const isSociopathAssessment = isAssessmentType(
    assessmentId,
    SOCIOPATH_TEST_ASSESSMENT_ID,
  );

  const isIqTestAssessment = isAssessmentType(
    assessmentId,
    IQ_TEST_ASSESSMENT_ID,
  );

  // TEMPORARY provisional classification — see assessment-utils.js
  // (computeProvisionalSociopathOverallPercentage/
  // computeProvisionalSociopathClassification). Shared with the full V2
  // report so both surfaces resolve the same category for the same
  // /result — not the real nearest_profile classifier.
  const sociopathData =
    isSociopathAssessment &&
    (() => {
      const overallPercentage = computeProvisionalSociopathOverallPercentage(
        currentUserTraits,
      );
      const classification =
        computeProvisionalSociopathClassification(overallPercentage);
      return getPanelData({
        isAssessmentEnabled: true,
        level: classification.categoryKey,
        panels: contentItems,
      });
    })();

  // Reuses the report's own scoring functions (iq-calculations.js) so the
  // overview never derives a different band than the full report for the
  // same result — no second IQ classification implementation. Section raw
  // scores come directly from each trait's own `score` field (the
  // backend's authoritative correct-answer count) — never reconstructed
  // from `percentage`.
  const iqData =
    isIqTestAssessment &&
    (() => {
      const overallRawScore = deriveOverallRawScore(
        (currentUserTraits || []).map((trait) =>
          trait.score != null ? Number(trait.score) : null,
        ),
      );
      const overallPercentage = deriveOverallPercentage(
        overallRawScore,
        TOTAL_QUESTIONS,
      );
      const bandKey = deriveBandKeyFromPercentage(overallPercentage);
      return getPanelData({
        isAssessmentEnabled: true,
        level: bandKey,
        panels: contentItems,
      });
    })();

  // The generic lead-in item ("Your overall result indicates the
  // following:") is IQ-specific — no other assessment's OVERVIEW_CONTENT
  // has this shape, so this lookup is not generalized.
  const iqDescriptionData =
    isIqTestAssessment &&
    contentItems.find(
      (item) =>
        item?.nameEnglishCode === "description" ||
        item?.personalityType === "description",
    );

  // Trait details and panel data - All calculations done here
  const bigFiveTraitDetailsWithLabels =
    isBigFiveAssessment &&
    getBigFiveTraitDetailsWithLabels(currentUserTraits, t);
  const dyslexiaChildrenWithLabel =
    isDyslexiaChlidrenAssessment &&
    getDyslexiaChildrenDetailsWithLabel(currentUserTraits, currentLocale, t);
  const dyslexiaAdultWithLabel =
    isDyslexiaAdultAssessment &&
    getDyslexiaAdultDetailsWithLabel(currentUserTraits, currentLocale, t);
  const loveStyleWithLabel =
    isLoveStyleAssessment &&
    getLoveStyleDetailsWithLabel(currentUserTraits, currentLocale);
  const personalityDisorderDetailWithLabel =
    isPersonalityDisorder &&
    getPersonalityDisorderDetailWithLabel(currentUserTraits, currentLocale);
  const romanticCompatibilityDetailWithLabel =
    isRomanticCompatibilityAssessment &&
    getRomanticCompatiblityDetailsWithLabel(
      currentUserTraits,
      currentLocale,
      t,
    );
  const likeabilityDetailWithLabel =
    isLikeabilityAssessment &&
    getLikeabilityDetailsWithLabel(currentUserTraits);

  // const psychopathDetailWithLable = isPsychopathTestAssessment &&

  const careerPreferenceDetailWithLabel =
    isCareerPreferenceAssessment &&
    generateContentKeyForCareerPreference(
      currentUserTraits,
      CAREER_PREFERENCE_TEST_ASSESSMENT_ID,
      currentLocale,
    );
  const bigFiveFacetsDetailWithLabel =
    isBigFiveFacetsAssessment &&
    calculateBigFiveFromFacets(currentUserTraits, t);

  // Panel data computations
  const adhdData =
    isAdhdAssessment &&
    (() => {
      const transformed = transformAdhdUserTraitsData(currentUserTraits);
      const level = getAdhdScoreCategory(transformed?.totalScore / 6);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const antiSocialData =
    isAntiSocialAssessment &&
    (() => {
      const transformed =
        transformAntisocialPersonalityDisorderUserTraitsData(currentUserTraits);
      const level = getAntisocialPersonalityDisorderLevel(
        transformed.avgTotalScore,
        t,
      );
      return getPanelData({
        isAssessmentEnabled: true,
        level: level?.englishCode?.toLowerCase().replace(" ", "_"),
        panels: contentItems,
      });
    })();

  const autismSpectrumData =
    isAutismSpectrumAssessment &&
    (() => {
      const transformed =
        transformAutismSpectrumUserTraitsData(currentUserTraits);
      const level = getAustismSpecturmOverAllLevelDetails(
        transformed.totalScore,
      );
      return getPanelData({
        isAssessmentEnabled: true,
        level: level?.level,
        panels: contentItems,
      });
    })();

  const bipolarData =
    isBipolarAssessment &&
    (() => {
      const score = calculateBipolarScore(currentUserTraits);
      const level = getBipolarResultCategory(score);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const careerPreferenceData =
    isCareerPreferenceAssessment &&
    careerPreferenceDetailWithLabel &&
    getPanelData({
      isAssessmentEnabled: true,
      level: careerPreferenceDetailWithLabel?.primary?.en,
      panels: contentItems,
    });

  const colorBlindData =
    isColorBlindAssessment &&
    (() => {
      const highTrait =
        findColorBlingHighestPercentageTrait(currentUserTraits)?.trait?.title;
      const traitKey = highTrait?.toLowerCase().replaceAll("_", "-");
      const level = traitKey ? toCamelCase(traitKey) : highTrait;
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const depressionData =
    isDepresssionAssessment &&
    (() => {
      const totalScore = calculateTotalScoreDepression(currentUserTraits);
      const level = getDepressionLevel(totalScore);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const difficultPersonData =
    isDifficultPersonAssessment &&
    (() => {
      const transformed =
        transformDifficultPersonUserTraitsData(currentUserTraits);
      const level = getDifficultyLevel(transformed?.totalScore);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const highlySensitivePersonData =
    isHighlySensitiveAssessment &&
    (() => {
      const transformed =
        transformHighlySensitiveUserTraitsData(currentUserTraits);
      const level = getHighlySensitiveLevel(transformed?.totalScore);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  const likeabilityData = likeabilityScore(currentUserTraits, currentLocale);

  const narcissisticPersonalityData =
    isNarcissisticPersonalityAssessment &&
    (() => {
      const transformed =
        transformNarcissisticPersonalityUserTraitsData(currentUserTraits);
      const level = getOverAllNarcissisticPersonalityLevel(
        transformed.totalScore,
        t,
      );
      return getPanelData({
        isAssessmentEnabled: true,
        level: level?.englishCode,
        panels: contentItems,
      });
    })();

  const emotionalIntelligenceData =
    isEmotionalIntelligenceAssessment &&
    (() => {
      const totalScore =
        getEmotioanlIntelligenceTraitScoresWithTotal(currentUserTraits);
      const level = getEmotioanlIntelligenceOverAllScoreLevel(totalScore);
      return getPanelData({
        isAssessmentEnabled: true,
        level,
        panels: contentItems,
      });
    })();

  // Filtered panels
  const filteredEnneagramPanel =
    isEnneagramAssessment &&
    contentItems.find(
      (panel) =>
        convertToLowerCase(panel.nameEnglishCode) ===
        convertToLowerCase(personalityNameEnglishCode),
    );
  const filteredPersonalityPanel =
    isPersonalityTestAssessment &&
    contentItems.find(
      (panel) =>
        convertToLowerCase(panel.nameEnglishCode) ===
        convertToLowerCase(personalityTitle),
    );

  const filteredCareerTestPanel =
    isCareerTestAssessment &&
    contentItems.find(
      (panel) =>
        convertToLowerCase(panel.personalityType) ===
        convertToLowerCase(personalityTitle),
    );

  const fourTemperamenTopTraitCombination =
    isFourTemperamentsAssessment &&
    fourTemperamentTraitCombination(currentUserTraits);
  const fourTemperamentData =
    isFourTemperamentsAssessment &&
    fourTemperamentFindPersonalityBlend(
      fourTemperamenTopTraitCombination,
      contentItems,
    );

  // Content data
  const contentData = getContentData(contentItems);

  const discDetailWithLabel =
    isDiscAssessment &&
    getDiscPersonalityDetailsWithLabel(currentUserTraits, contentData);

  const attachmentStyleScoreList = attachmentStyleScore(
    currentUserTraits,
    currentLocale,
  );

  const psychopathWithLabel =
    isPsychopathTestAssessment &&
    getPsychopathDetailWithLable(currentUserTraits);

  const childhoodTraumaScoreList = childhoodTraumaScore(
    currentUserTraits,
    currentLocale,
  );
  const socialAnxietyScoreList = socialAnxietyScore(
    currentUserTraits,
    currentLocale,
  );

  const autismChildScoreList = autismChildrenScore(
    currentUserTraits,
    currentLocale,
  ).sections;

  return (
    <div className="flex flex-col w-full justify-between items-start px-2 flex-grow">
      {/* Personality Label */}
      <div className="flex flex-col w-full justify-start items-start flex-grow">
        <p className="mt-3 text-[#805AED] text-sm font-medium">
          {t("dashboard-b.overview.personality_type")}
        </p>
        {/* Title */}
        <h2 className="font-montserrat font-bold text-lg text-left w-full">
          {adhdData?.personalityName && (
            <span className="text-[#444C5E]">
              {adhdData?.personalityName || ""}
            </span>
          )}
          {antiSocialData?.personalityName && (
            <span className="text-[#444C5E]">
              {antiSocialData?.personalityName || ""}
            </span>
          )}
          {autismSpectrumData?.personalityName && (
            <span className="text-[#444C5E]">
              {autismSpectrumData?.personalityName || ""}
            </span>
          )}
          {bipolarData?.personalityName && (
            <span className="text-[#444C5E]">
              {bipolarData?.personalityName || ""}
            </span>
          )}
          {colorBlindData?.personalityName && (
            <span className="text-[#444C5E]">
              {colorBlindData?.personalityName || ""}
            </span>
          )}
          {depressionData?.personalityName && (
            <span className="text-[#444C5E]">
              {depressionData?.personalityName || ""}
            </span>
          )}
          {difficultPersonData?.personalityName && (
            <span className="text-[#444C5E]">
              {difficultPersonData?.personalityName || ""}
            </span>
          )}
          {highlySensitivePersonData?.personalityName && (
            <span className="text-[#444C5E]">
              {highlySensitivePersonData?.personalityName || ""}
            </span>
          )}
          {narcissisticPersonalityData?.personalityName && (
            <span className="text-[#444C5E]">
              {narcissisticPersonalityData?.personalityName || ""}
            </span>
          )}
          {emotionalIntelligenceData?.personalityName && (
            <span className="text-[#444C5E]">
              {emotionalIntelligenceData?.personalityName || ""}
            </span>
          )}
          {isSociopathAssessment && sociopathData?.personalityName && (
            <span className="text-[#444C5E]">
              {sociopathData?.personalityName || ""}
            </span>
          )}
          {isIqTestAssessment && iqData?.personalityName && (
            <span className="text-[#444C5E]">
              {iqData?.personalityName || ""}
            </span>
          )}

          {filteredEnneagramPanel?.personalityType && (
            <span className="font-normal text-[#805AED]">
              {filteredEnneagramPanel?.personalityType}
            </span>
          )}
          {filteredPersonalityPanel?.personalityType && (
            <span className="font-normal text-[#805AED]">
              {filteredPersonalityPanel?.personalityType}
            </span>
          )}
        </h2>
        {/* Description */}
        <p className="text-left text-[14px] mt-1 leading-relaxed">
          {filteredEnneagramPanel?.content && filteredEnneagramPanel?.content}
          {filteredPersonalityPanel?.content &&
            filteredPersonalityPanel?.content}
          {filteredCareerTestPanel?.content && filteredCareerTestPanel?.content}
          {adhdData?.content && adhdData?.content}
          {antiSocialData?.content && antiSocialData?.content}
          {autismSpectrumData?.content && autismSpectrumData?.content}
          {bipolarData?.content && bipolarData?.content}
          {colorBlindData?.content && colorBlindData?.content}
          {depressionData?.content && depressionData?.content}
          {difficultPersonData?.content && difficultPersonData?.content}
          {highlySensitivePersonData?.content &&
            highlySensitivePersonData?.content}

          {narcissisticPersonalityData?.content &&
            narcissisticPersonalityData?.content}
          {isDyslexiaAdultAssessment && contentData}
          {isDyslexiaChlidrenAssessment && contentData}
          {isBigFiveAssessment && contentData}
          {isBigFiveFacetsAssessment && contentData}
          {isLoveStyleAssessment && contentData}
          {isPersonalityDisorder && contentData}
          {isRomanticCompatibilityAssessment && contentData}
          {isDiscAssessment && discDetailWithLabel}
          {isAttachmentStyle && contentData}
          {isFourTemperamentsAssessment &&
            fourTemperamentData?.content &&
            fourTemperamentData?.content}
          {isEmotionalIntelligenceAssessment &&
            emotionalIntelligenceData?.content &&
            emotionalIntelligenceData?.content}
          {isLikeabilityAssessment && contentData}
          {isPsychopathTestAssessment && contentData}
          {isPsychopathTestAssessment && contentData}
          {isChildhoodTrauma && contentData}
          {isSocialAnxiety && contentData}
          {isAutismChild && contentData}
          {isSociopathAssessment && sociopathData?.content}
          {isIqTestAssessment && iqDescriptionData?.content}
          {isIqTestAssessment && iqData?.content}

          {/* Psychopath Test Scores */}
          {isPsychopathTestAssessment && psychopathWithLabel && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              <div>
                <span className="font-semibold">
                  {" "}
                  {t("report.likeability.overall_score_label")}:
                </span>{" "}
                <span>{psychopathWithLabel.overallScore}</span>
              </div>
              <div>
                <span className="font-semibold">
                  {" "}
                  {t("report.psychopathy.sociopathy_score")}:
                </span>{" "}
                <span>{psychopathWithLabel.sociopathyScore}</span>
              </div>
              <div>
                <span className="font-semibold">
                  {" "}
                  {t("report.psychopathy.impulsivity_score")}:
                </span>{" "}
                <span>{psychopathWithLabel.impulsivityScore}</span>
              </div>
            </div>
          )}
        </p>
        {isLikeabilityAssessment && (
          <p className="text-left text-[14px] mt-1 leading-relaxed">
            <span className="font-bold">
              {t("report.likeability.overall_score_label")}:
            </span>{" "}
            &nbsp;
            {likeabilityDetailWithLabel}
          </p>
        )}
        {/* Trait Details */}
        {isBigFiveAssessment &&
          bigFiveTraitDetailsWithLabels &&
          bigFiveTraitDetailsWithLabels.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {bigFiveTraitDetailsWithLabels.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {isBigFiveFacetsAssessment &&
          bigFiveFacetsDetailWithLabel &&
          bigFiveFacetsDetailWithLabel.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {bigFiveFacetsDetailWithLabel.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {isDyslexiaChlidrenAssessment &&
          dyslexiaChildrenWithLabel &&
          dyslexiaChildrenWithLabel.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {dyslexiaChildrenWithLabel.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {isDyslexiaAdultAssessment &&
          dyslexiaAdultWithLabel &&
          dyslexiaAdultWithLabel.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {dyslexiaAdultWithLabel.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {isLoveStyleAssessment &&
          loveStyleWithLabel &&
          loveStyleWithLabel &&
          loveStyleWithLabel?.scores.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              <p>Overall scores:</p>
              {loveStyleWithLabel?.scores.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.percentage}%</span>
                </div>
              ))}
            </div>
          )}
        {isAttachmentStyle &&
          attachmentStyleScoreList &&
          attachmentStyleScoreList.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {attachmentStyleScoreList?.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.percentage}%</span>
                </div>
              ))}
            </div>
          )}
        {isLikeabilityAssessment &&
          likeabilityData &&
          likeabilityData.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {likeabilityData?.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.percentage}%</span>
                </div>
              ))}
            </div>
          )}
        {isPersonalityDisorder &&
          personalityDisorderDetailWithLabel &&
          personalityDisorderDetailWithLabel.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {personalityDisorderDetailWithLabel.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {isRomanticCompatibilityAssessment &&
          romanticCompatibilityDetailWithLabel &&
          romanticCompatibilityDetailWithLabel.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {romanticCompatibilityDetailWithLabel.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.level}</span>
                </div>
              ))}
            </div>
          )}
        {careerPreferenceDetailWithLabel && (
          <>
            <div className="text-left text-[14px] mt-2 space-y-1">
              <h5 className="font-semibold">
                {t("report.career_preference_precontent.primary_label")}
              </h5>
              {snakeToTitleCase(
                careerPreferenceDetailWithLabel.primary.localized,
              )}{" "}
              : &nbsp;
              <span>
                {careerPreferenceData?.content && careerPreferenceData?.content}
              </span>
            </div>
            <div className="text-left text-[14px] mt-2 space-y-1">
              <h5 className="font-semibold">
                {" "}
                {t("report.career_preference_precontent.secondary_label")}
              </h5>
              {careerPreferenceDetailWithLabel.secondary.map((item) => {
                return <p>{snakeToTitleCase(item.localized)} </p>;
              })}{" "}
            </div>
          </>
        )}
        {isChildhoodTrauma &&
          childhoodTraumaScoreList &&
          childhoodTraumaScoreList.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {childhoodTraumaScoreList?.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.score}</span>
                </div>
              ))}
            </div>
          )}
        {isSocialAnxiety &&
          socialAnxietyScoreList &&
          socialAnxietyScoreList.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {socialAnxietyScoreList?.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.score}</span>
                </div>
              ))}
            </div>
          )}
        {isAutismChild &&
          autismChildScoreList &&
          autismChildScoreList.length > 0 && (
            <div className="text-left text-[14px] mt-2 space-y-1">
              {autismChildScoreList?.map((item, index) => (
                <div key={index}>
                  <span className="font-semibold">{item.label}:</span>{" "}
                  <span>{item.score}</span>
                </div>
              ))}
            </div>
          )}
      </div>
      {/* View Personalized Report */}
      <button
        className="mt-5 rounded-full font-bold text-white mx-auto block
    px-4 py-3 text-center whitespace-normal break-words leading-tight
    text-[14px] sm:text-[16px] w-[75%] bg-[#805AED]"
        onClick={() =>
          handleViewReport(
            isPremiumOrGuest,
            selectedAssessment?.assessmentAttemptId,
            selectedAssessment?.assessmentId,
            selectedAssessment?.id,
            selectedAssessment?.reportType,
          )
        }
      >
        {t("dashboard-b.personality.view_report_btn")}
      </button>
    </div>
  );
};
